import axios from 'axios';
import { refreshSession } from './sessionRefresh';
import { API_BASE } from '../utils/apiBase';
import { getActivePage } from './activePage';
import { clearRouteData } from '../data/routeData';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add JWT token + the Pro page persona
api.interceptors.request.use(
  (config) => {
    if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())
      && !/analytics|\/track|\/presence|\/viewed|\/read|\/refresh|subscribe/i.test(config.url || '')) clearRouteData();
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Acting as a Pro page: the server resolves owner-scoped reads and writes
    // (/pro/me, /pro/vendor-offers, /pro/analytics, …) to this page instead of
    // the account's first one. Absent when no persona is stored.
    const pageId = getActivePage()?.id;
    if (pageId && !config.headers['X-Pro-Page']) {
      config.headers['X-Pro-Page'] = pageId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for 401 handling and refresh token flow
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh for auth endpoints (login, register, etc.)
    // These 401s are legitimate "invalid credentials" responses, not expired tokens
    const requestUrl = originalRequest.url || '';
    const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // A truly anonymous visitor (never had a session) must NOT be yanked to
        // /login just because a public page fired an auth-only call that 401'd —
        // that's what broke logged-out access to /featured. Only redirect when a
        // real session existed and has now expired.
        const hadSession = !!localStorage.getItem('accessToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        isRefreshing = false;
        if (hadSession) {
          const currentPath = window.location.pathname + window.location.search;
          window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
        }
        return Promise.reject(error);
      }

      // ONE refresh for the whole app. `refreshSession` shares its in-flight promise
      // with AuthContext, so the boot refresh and a 401'd API call in the same second
      // present the single-use token ONCE. Two at a time used to earn the loser a 500
      // (the row the winner had just rotated) and spend two slots of the auth throttle.
      const result = await refreshSession();
      isRefreshing = false;

      if (result.accessToken) {
        api.defaults.headers.common.Authorization = `Bearer ${result.accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
        processQueue(null, result.accessToken);
        return api(originalRequest);
      }

      const refreshError = result.error
        || Object.assign(new Error('Token refresh unavailable'), { response: result.status ? { status: result.status, data: {} } : undefined });

      // A deploy restarts the API for a few seconds and nginx answers 502 while it
      // does, and every open tab reloads at the same moment (the service worker's
      // controllerchange handler). Reading that as "your refresh token is bad" is
      // what used to sign people out mid-deploy — the token in the database was
      // fine. The same goes for the auth throttle's 429. Only the server actually
      // REJECTING the token ends the session; anything else leaves it in place and
      // the next call tries again.
      if (!result.rejected) {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }

      // Rotation is single-use, so two tabs reloading together both present the
      // same token and the loser gets a 401 for a token the winner already
      // replaced. If the stored token has moved on under us, that's what
      // happened — pick up the winner's token instead of ending the session.
      const current = localStorage.getItem('refreshToken');
      if (current && current !== refreshToken && !originalRequest._rotated) {
        originalRequest._rotated = true;
        originalRequest._retry = false;
        processQueue(refreshError, null);
        return api(originalRequest);
      }

      processQueue(refreshError, null);

      // Clear auth data and redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;

      return Promise.reject(refreshError);
    }

    // 403 PROFILE_REQUIRED — the account has a user row but no profile, so the server refuses
    // every call. Send them to the one page that can fix it.
    //
    // This used to hard-navigate to `/credentials`, a route that does not exist and never did:
    // it fell through App.js's catch-all to `/`, bounced on to `/onboard` or `/featured`, whose
    // first API call 403'd the same way and navigated again — an endless loop of full page
    // loads, each one replaying the portal. The guard matters as much as the destination:
    // `/onboard` keeps polling (notifications, app notices) and would loop just the same.
    if (error.response?.status === 403) {
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'PROFILE_REQUIRED') {
        if (!window.location.pathname.startsWith('/onboard')) window.location.href = '/onboard';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to handle API responses
export const handleResponse = (response) => {
  // API may return { success, data, error } or raw data directly
  const apiResponse = response.data;

  // If wrapped format with explicit data property, use it
  if (apiResponse && typeof apiResponse === 'object' && 'data' in apiResponse) {
    return {
      success: apiResponse.success !== false,
      data: apiResponse.data,
      error: apiResponse.error?.message || null,
    };
  }

  // Otherwise, treat the response as raw data
  return {
    success: true,
    data: apiResponse,
    error: null,
  };
};

// Helper function to handle API errors
export const handleError = (error) => {
  const data = error.response?.data;
  const errField = data?.error;

  // ASP.NET model-validation 400s come back as ValidationProblemDetails:
  //   { title, status, errors: { Field: ["message", ...] } }
  // There's no top-level message/error, so pull the first field message out —
  // e.g. "Minimum withdrawal is $10.00" — instead of falling through to the
  // generic axios "Request failed with status code 400".
  let validationMessage = null;
  if (data?.errors && typeof data.errors === 'object') {
    const firstMessages = Object.values(data.errors).find((v) => Array.isArray(v) && v.length > 0);
    if (firstMessages) validationMessage = firstMessages[0];
  }

  const message =
    validationMessage ||
    data?.message ||
    (typeof errField === 'string' ? errField : errField?.message) ||
    data?.title ||
    error.message ||
    'An unexpected error occurred';

  return {
    success: false,
    data: null,
    error: message,
    // Machine-readable error code (ApiError.Code) when present, for callers that
    // branch on specific failures (e.g. EMAIL_REGISTERED).
    code: (typeof errField === 'object' ? errField?.code : null) || null,
    status: error.response?.status || null,
    // No HTTP response at all → the request never reached the server (offline,
    // DNS, timeout). Lets callers keep the user's input and retry on reconnect
    // instead of treating it like a server rejection.
    isNetwork: !error.response,
  };
};

export default api;
